import React, { useState } from 'react';
import cx from 'classnames';
import { intersection, omitBy, pickBy } from 'lodash';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { FilterConfig } from '@magoosh/organisms/filters/types';
import RatingForm from '@magoosh/organisms/rating';
import { paths } from 'config/path_helpers';
import { toSentence } from 'utilities/formatting';

import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';
import ContextualLink from '@test_prep/app/components/contextual_link';

import EstimatedScore from './components/estimated_score';
import FilterManager from '../components/filter_manager';
import PaceStat from '../components/pace_stat';
import PerformanceByDifficultyChart from '../components/performance_by_difficulty_chart';
import PerformanceBySectionChart from './components/performance_by_section_chart';
import PerformanceByTypeChart from '../components/performance_by_type_chart';
import PerformanceChart from '../components/performance_chart';
import QuestionsStat from '../components/questions_stat';
import StudyTimeChart from '../components/study_time_chart';

import FlashcardsMasteredChart from './components/flashcards_mastered_chart';
import SectionSubjects from './components/section_subjects';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from '../style.module.scss';

interface Props {}

const Analytics: React.FC<Props> = (props) => {
  const { branding, features, filters } = useAppContext().data;
  const history = useHistory();
  const q = useQueries();
  const [hideSubjectsWithNoActivity, setHideSubjectsWithNoActivity] = useState(false);

  const page = q.pageQuery(paths.analytics()).data;

  const breadcrumbs = [
    { name: 'Home', path: paths.dashboard() },
    { name: 'Analytics' }
  ];

  const filtersConfig = [
    {
      all: true,
      allowWhileFocused: true,
      className: 'flex-1',
      default: 'all',
      multiple: true,
      multipleStyle: 'button',
      key: 'sections',
      name: 'Section',
      options: (page?.sections || []).map((section) => (
        { name: section.name, value: section.slug }
      ))
    },
    {
      default: 'all_time',
      key: 'period',
      name: 'Period',
      options: [
        { name: 'All Time', value: 'all_time' },
        { name: 'Custom', value: 'custom' },
        { name: 'Since Last Practice Test', value: 'since_last_practice_test' }
      ]
    }
  ] as FilterConfig[];

  const displayPercentageGradingType = () => {
    return sectionsToDisplayGradedByPercentage().length > 0;
  };

  const displayValueGradingType = () => {
    return sectionsToDisplayGradedByValue().length > 0;
  };

  const extractRelevantFilters = () => {
    if (!filters) return null;

    const relevantKeys = filtersConfig.map((filterConfig) => filterConfig.key).concat('focus');
    return pickBy(filters, (_, key) => relevantKeys.indexOf(key) !== -1);
  };

  const handleFiltersChanged = (updatedFilters) => {
    history.replace(paths.analytics(omitBy(updatedFilters, (value) => !value)));
  };

  const sectionsToDisplay = () => {
    if (!page || !relevantFilters?.sections) return [];

    if (relevantFilters.sections === 'all') {
      return page.sections;
    } else {
      const slugs = (relevantFilters.sections as string).split(',');
      return page.sections.filter((section) => slugs.indexOf(section.slug) !== -1);
    }
  };

  const sectionsToDisplayGradedByPercentage = () => {
    return sectionsToDisplay().filter((section) => (
      section.gradingTypes.indexOf('percentage') !== -1
    ));
  };

  const sectionsToDisplayGradedByValue = () => {
    return sectionsToDisplay().filter((section) => (
      section.gradingTypes.indexOf('value') !== -1
    ));
  };

  const relevantFilters = extractRelevantFilters();

  return (
    <>
      <Helmet>
        <title>Analytics - Magoosh {branding.examName}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs} linkComponent={ContextualLink} loading={!filters}>
        {({ loading }) => {
          if (loading) return;

          return (
            <div className="d-flex flex-column gap-m">
              {
                features.estimatedScore && !filters.focus && (
                  <div className="d-flex flex-column flex-lg-row gap-m">
                    <CardLayout.Card className="d-flex flex-1 flex-column gap-m" style={{ maxWidth: '100%' }}>
                      <EstimatedScore examName={branding.examName} filters={{ period: 'all_time' }} />
                    </CardLayout.Card>
                  </div>
                )
              }

              <div className="d-flex flex-column gap-s gap-lg-m">
                <hr className="u-margin-A-n" />

                <FilterManager
                  filtersConfig={filtersConfig}
                  onChange={handleFiltersChanged}
                />

                <hr className="u-margin-A-n" />
              </div>

              <div className="d-flex flex-column gap-m">
                <div className="d-flex flex-column gap-m">
                  <div className="d-flex flex-column gap-s gap-lg-m">
                    <div className="align-items-end d-flex">
                      <h3 className="flex-1 u-margin-A-n u-margin-T-s"><strong>Performance and Study Activity</strong></h3>
                    </div>

                    <hr className="u-margin-A-n" />
                  </div>

                  <div className="d-flex flex-1 flex-column flex-sm-row flex-sm-wrap gap-m">
                    <div className="d-flex flex-column gap-m">
                      <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                        <h3 className="u-margin-A-n">Questions</h3>

                        <QuestionsStat examName={branding.examName} filters={relevantFilters} />
                      </CardLayout.Card>

                       <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                        <h3 className="u-margin-A-n">Pace</h3>

                        <PaceStat examName={branding.examName} filters={relevantFilters} />
                      </CardLayout.Card>
                    </div>

                    <div className="d-flex flex-column gap-m">
                      {
                        displayPercentageGradingType() && (
                          <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                            <div className="align-items-center d-flex flex-column">
                              <h3 className="u-margin-A-n">Performance</h3>

                              {
                                displayValueGradingType() && (
                                  <small className="text-center">
                                    {toSentence(sectionsToDisplayGradedByPercentage().map((section) => section.name))}
                                  </small>
                                )
                              }
                            </div>

                            <PerformanceChart
                              examName={branding.examName}
                              filters={relevantFilters}
                              gradingType="percentage"
                              sections={sectionsToDisplayGradedByPercentage()}
                              size={displayValueGradingType() ? 'sm' : 'lg'}
                            />
                          </CardLayout.Card>
                        )
                      }

                      {
                        displayValueGradingType() && (
                          <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                            <div className="align-items-center d-flex flex-column">
                              <h3 className="u-margin-A-n">Performance</h3>

                              {
                                displayPercentageGradingType() && (
                                  <small className="text-center">
                                    {toSentence(sectionsToDisplayGradedByValue().map((section) => section.name))} (Graded)
                                  </small>
                                )
                              }
                            </div>

                            <PerformanceChart
                              examName={branding.examName}
                              filters={relevantFilters}
                              gradingType="value"
                              sections={sectionsToDisplayGradedByValue()}
                              size={displayPercentageGradingType() ? 'sm' : 'lg'}
                            />
                          </CardLayout.Card>
                        )
                      }
                    </div>

                    <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                      <h3 className="u-margin-A-n">Study Time</h3>
                      <StudyTimeChart examName={branding.examName} filters={relevantFilters} />
                    </CardLayout.Card>

                    {
                      !filters.focus && (
                        <CardLayout.Card className={cx(style.card4Col, 'align-items-center d-flex flex-1 flex-column gap-m')}>
                          <h3 className="u-margin-A-n">Flashcards</h3>
                          <FlashcardsMasteredChart examName={branding.examName} filters={relevantFilters} />
                        </CardLayout.Card>
                      )
                    }
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row flex-sm-wrap gap-m">
                  <CardLayout.Card className={cx(style.card2Col, 'd-flex flex-1 flex-column gap-m')}>
                    <h3 className="u-margin-A-n">Performance by Question Difficulty</h3>

                    <PerformanceByDifficultyChart examName={branding.examName} filters={relevantFilters} />
                  </CardLayout.Card>

                  {
                    // ACT, SAT, and TOEFL don't have enough question type data to be useful,
                    // so instead show by section
                    ['ACT', 'SAT', 'TOEFL'].indexOf(branding.examName) === -1 ? (
                      <CardLayout.Card className={cx(style.card2Col, 'd-flex flex-1 flex-column gap-m')}>
                        <h3 className="u-margin-A-n u-margin">Performance by Question Type</h3>

                        <PerformanceByTypeChart
                          examName={branding.examName}
                          filters={relevantFilters}
                          sections={relevantFilters.sections}
                        />
                      </CardLayout.Card>
                    ) : (
                      <CardLayout.Card className={cx(style.card2Col, 'd-flex flex-1 flex-column gap-m')}>
                        <h3 className="u-margin-A-n u-margin">Performance by Section</h3>

                        <PerformanceBySectionChart
                          examName={branding.examName}
                          filters={relevantFilters}
                        />
                      </CardLayout.Card>
                    )
                  }
                </div>
              </div>

              <div className="d-flex flex-column gap-m">
                <div className="d-flex flex-column gap-s gap-lg-m">
                  <div className="align-items-end d-flex">
                    <h3 className="flex-1 u-margin-A-n u-margin-T-s"><strong>Subjects</strong></h3>

                    <div className="align-items-center d-flex gap-xs hidden-xs">
                      <input
                        className={style.checkbox}
                        defaultChecked={hideSubjectsWithNoActivity}
                        id="hideSubjectsWithNoActivity"
                        onChange={(e) => setHideSubjectsWithNoActivity(e.target.checked)}
                        type="checkbox"
                      />
                      <label className="small u-margin-A-n" htmlFor="hideSubjectsWithNoActivity" style={{ fontWeight: 400 }}>
                        Hide subjects with no activity
                      </label>
                    </div>
                  </div>

                  <hr className="u-margin-A-n" />
                </div>

                <div className="d-flex flex-column gap-l" style={{ minHeight: 100, position: 'relative' }}>
                  {
                    page ? (
                      sectionsToDisplay().map((section, idx) => (
                        <SectionSubjects
                          key={idx}
                          examName={branding.examName}
                          filters={{ ...relevantFilters, sections: section.slug }}
                          hideSubjectsWithNoActivity={hideSubjectsWithNoActivity}
                        />
                      ))
                    ) : (
                      <div className="d-flex justify-content-center">
                        <LoadingIndicator />
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          );
        }}
      </CardLayout>
    </>
  );
};

export default Analytics;
